<template>
  <v-container fluid>
    <inventory-search-filter
      v-if="!mostRecent"
      :billing-status="query.billingStatus"
      :active-status="query.activeStatus"
      :description="query.description"
      :client="client"
      :sub-client="subClient"
      @filtersSelected="doSearch"
    />
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="searchPending"
      :no-data-text="includeInactive || query.searchText || query.description
        ? 'No items match. Try fewer words or open Show Search Options.' : 'No data available'"
      :options.sync="options"
      :server-items-length="totalItems"
      :footer-props="{'items-per-page-options': mostRecent ? [10] : [25, 50, 100]}"
      :hide-default-footer="mostRecent"
      loading-text="loading, please wait"
      class="elevation-1"
      @click:row="editItem"
    >
      <template v-slot:progress>
        <v-progress-linear
          v-show="searchPending"
          indeterminate
          rounded
          height="10"
        />
      </template>
      <template v-slot:top>
        <v-text-field
          v-if="!mostRecent"
          v-model="search"
          label="Search all inventory"
          hint="Add the word inactive to include delivered items.
            Start a word with - to leave matches out, like pew -pewter"
          persistent-hint
          prepend-inner-icon="mdi-magnify"
          clearable
          class="mx-4 mb-6"
        />
        <div
          v-if="includeInactive"
          class="caption grey--text text--darken-1 mx-4 mb-2"
        >
          Including inactive and billed items
        </div>
      </template>
      <template v-slot:item.receivedDate="{ item }">
        {{ item.receivedDate.substr(0, 10) }}
      </template>
      <template v-slot:item.billed="{ item }">
        <v-simple-checkbox
          :value="item.active"
          disabled
        />
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import InventorySearchFilter from './InventorySearchFilter.vue';

export default {
  name: 'InventoryList',
  components: {
    InventorySearchFilter,
  },
  props: {
    clientId: {
      type: String,
      default: null,
    },
    subClientId: {
      type: String,
      default: null,
    },
    pairClientId: {
      type: String,
      default: null,
    },
    pairSubClientId: {
      type: String,
      default: null,
    },
    mostRecent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: '',
    includeInactive: false,
    client: null,
    subClient: null,
    totalItems: 0,
    options: {
      page: 1,
      itemsPerPage: 50,
      sortBy: ['icn'],
      sortDesc: [false],
    },
    query: {
      startDate: null,
      endDate: null,
      clientId: null,
      subClientId: null,
      mfgId: null,
      billingStatus: null,
      locationStatus: null,
      pairClientId: null,
      pairSubClientId: null,
      activeStatus: null,
      searchText: null,
      description: null,
    },
    searchPending: false,
    searchTimerId: null,
    searchRequestId: 0,
    tableReady: false,
    headers: [
      {
        text: 'ICN',
        value: 'icn',
      },
      {
        text: 'Received',
        value: 'receivedDate',
      },
      {
        text: 'Description',
        value: 'description',
        sortable: false,
      },
      {
        text: 'Manufacturer',
        value: 'manufacturer.name',
      },
      {
        text: 'Client',
        value: 'client.name',
      },
      {
        text: 'SubClient',
        value: 'subClient.name',
      },
      {
        text: 'Photos & Files',
        value: 'assetCount',
        sortable: false,
      },
      {
        text: 'Active',
        value: 'billed',
        sortable: false,
      },
      {
        text: '',
        value: 'actions',
        sortable: false,
      },
    ],
    items: [],
    startDate: '',
    endDate: '',
  }),
  watch: {
    search() {
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.applyTextSearch();
      }, 400);
    },
    startDateMenu() {
      this.searchByDate();
    },
    endDateMenu() {
      this.searchByDate();
    },
    options: {
      handler() {
        if (this.tableReady && !this.mostRecent) {
          this.searchInventory();
        }
      },
    },
    deep: true,
  },
  created() {
    this.loading = true;
    // this.query.locationStatus = 'In Warehouse';
    // this.query.billingStatus = false;
    this.query.activeStatus = true;

    const clientId = this.getClientId();
    this.query.clientId = clientId;
    this.getClient(clientId);

    if (this.subClientId) {
      this.query.subClientId = this.subClientId;
      this.getSubClient(this.subClientId);
    }

    if (this.pairClientId && this.pairSubClientId) {
      this.query.pairClientId = this.pairClientId;
      this.query.pairSubClientId = this.pairSubClientId;

      this.getClient(this.pairClientId);
      this.getSubClient(this.pairSubClientId);
    }

    if (this.mostRecent) {
      this.options.itemsPerPage = 10;
    }
  },
  mounted() {
    this.tableReady = true;
    this.searchInventory();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimerId);
  },
  methods: {
    ...mapActions('apiClient', ['get', 'post']),
    ...mapGetters('auth', ['getClientId']),
    editItem(item) {
      const route = this.$router.resolve({ name: 'inventoryEdit', params: { itemId: item.itemId } });
      window.open(route.href, '_blank');
    },
    async doSearch(query) {
      this.query = {
        ...this.query,
        ...query,
        searchText: this.normalizedSearchText(),
      };
      if (this.options.page === 1) {
        await this.searchInventory();
      } else {
        this.options.page = 1;
      }
    },
    normalizedSearchText() {
      const tokens = (this.search || '').trim().split(/\s+/);
      this.includeInactive = tokens.some((token) => token.toLowerCase() === 'inactive');
      const searchText = tokens.filter((token) => token.toLowerCase() !== 'inactive').join(' ');
      return searchText || null;
    },
    async applyTextSearch() {
      clearTimeout(this.searchTimerId);
      this.searchTimerId = null;
      this.query.searchText = this.normalizedSearchText();

      if (this.options.page === 1) {
        await this.searchInventory();
      } else {
        this.options.page = 1;
      }
    },
    async getClient(clientId) {
      await this.get({ url: `/clients/${clientId}` }).then(
        (response) => {
          this.client = response.data;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async getSubClient(clientId) {
      await this.get({ url: `/clients/${clientId}` }).then(
        (response) => {
          this.subClient = response.data;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async searchInventory() {
      this.searchRequestId += 1;
      const requestId = this.searchRequestId;
      this.searchPending = true;

      const page = Math.max(this.options.page - 1, 0);
      const size = this.options.itemsPerPage;
      const sortBy = this.options.sortBy.length > 0 ? this.options.sortBy[0] : 'icn';
      const sortDesc = this.options.sortDesc.length > 0 ? this.options.sortDesc[0] : false;
      const searchUrl = `/inventory/search?page=${page}&size=${size}&sortBy=${encodeURIComponent(sortBy)}&sortDesc=${sortDesc}&sortOverride=true`;
      const url = this.mostRecent ? `/inventory/recent/${this.query.clientId}` : searchUrl;

      const body = {
        ...this.query,
        searchText: this.normalizedSearchText(),
        activeStatus: this.includeInactive ? null : this.query.activeStatus,
        billingStatus: this.includeInactive ? null : this.query.billingStatus,
      };
      await this.post({ url, body }).then(
        (response) => {
          if (requestId !== this.searchRequestId) {
            return;
          }
          this.items = response.data.data;
          this.totalItems = response.data.totalElements;
        },
        (error) => {
          console.log(error);
        },
      );

      if (requestId === this.searchRequestId) {
        this.searchPending = false;
      }
    },
  },
};
</script>
